import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./styles.module.scss";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { some } from "lodash";
import Filters from "../Filters";
import ReportHeader from "./ReportHeader";
import ReportBody from "./ReportBody";
import ReportTotals from "./ReportTotals";
import ReportPagination from "./ReportPagination";
import FileFormatModal from "../Modals/FileFormatModal";
import { useTranslation } from "react-i18next";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import { calculateTableContainerHeight } from "./ReportBody/helper";

const ReportContent = ({ report }) => {
  const { reportId } = useParams<{ reportId: string }>();
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const history = useHistory();
  const [maxNumberOfPages, setMaxNumberOfPages] = useState(null);
  const [reportTotals, setReportTotals] = useState(null);
  const [reportRows, setReportRows] = useState([]);
  const [loadingReportRows, setLoadingReportRows] = useState(false);
  const [loadingReportTotals, setLoadingReportTotals] = useState(true);
  const [loadingReportPagination, setLoadingReportPagination] = useState(true);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const { t } = useTranslation();

  const requiredFilters = useMemo(() => {
    return report.filters.filter((f) => f.required);
  }, [report]);

  useEffect(() => {
    const newParams = new URLSearchParams(location.search);

    requiredFilters.forEach((f) => {
      if (!newParams.has(f.name)) {
        newParams.set(f.name, f.default);
      }
    });

    if (!newParams.has("sort_by")) {
      const defaultSortColumn = report.columns.find((c) => c.default_sortable);

      if (defaultSortColumn) {
        newParams.set("sort_by", defaultSortColumn.fieldName);
        newParams.set("sort_order", "asc");
      }
    }

    if (!newParams.has("page")) {
      newParams.set("page", "1");
      newParams.set("per_page", "20");
    }

    if (newParams.toString() !== queryParams.toString()) {
      history.replace({ pathname: location.pathname, search: `?${newParams.toString()}` });
    }
  }, []);

  useEffect(() => {
    if (!reportId || loadingReportRows) {
      return;
    }

    if (some(requiredFilters, (f) => !queryParams.get(f.name))) {
      return;
    }

    setLoadingReportRows(true);
    if (!reportTotals) {
      setLoadingReportTotals(true);
    }

    if (!maxNumberOfPages) {
      setLoadingReportPagination(true);
    }

    schedulingFetchGet<{ headers: Headers; students: { rows: any[]; totals: any[] } }>(
      `reports/${reportId}/rows`,
      { ...Object.fromEntries(queryParams.entries()), include_totals: true },
      { withHeaders: true }
    )
      .then((response: { headers: Headers; students: { rows: any[]; totals: any[] } }) => {
        setReportRows(response.students.rows);

        if (!reportTotals) {
          setReportTotals(response.students.totals);
          setLoadingReportTotals(false);
        }

        const totalPages = response.headers.get('total-pages');
        if (totalPages) {
          setMaxNumberOfPages(parseInt(totalPages, 10));
        }

        setLoadingReportPagination(false);
        setLoadingReportRows(false);
      })
      .catch(() => {
        history.push('/404');
        setLoadingReportRows(false);
      });
  }, [queryParams]);

  const addParamsToQuery = useCallback(
    (newValues) => {
      const newParams = new URLSearchParams(queryParams);

      Object.entries(newValues).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          newParams.delete(key);
        } else {
          newParams.set(key, value.toString());
        }
      });

      if (Object.keys(newValues).some((key) => report.filters.some((f) => f.name === key))) {
        newParams.set("page", "1");
      }

      setMaxNumberOfPages(null);
      setReportTotals(null);

      history.push({ pathname: location.pathname, search: `?${newParams.toString()}` });
    },
    [queryParams, requiredFilters]
  );

  return (
    <>
      <div className={styles.report_container}>
        <div className={styles.title_container}>
          <div className={styles.first_half_title}>
            <button
              className={styles.back_button}
              onClick={() => history.push('/reports')}
            >
              <i className="ico ico-back" />
            </button>
            <h1 className={styles.big_title}>{t(report.title)}</h1>
            <h1 className={styles.small_title}>{t(report.shortTitle)}</h1>
          </div>
          <button
            className={styles.download_button}
            onClick={() => setShowDownloadModal(true)}
          >
            <i className="ico ico-download" />
            <p>{t('Download')}</p>
          </button>
        </div>
        <div className={`${styles.container} calendar-page-container`}>
          {report.filters?.length > 0 && (
            <div className={styles.filters_container}>
              <Filters
                reportId={reportId}
                filters={report.filters}
                values={queryParams}
                onChange={addParamsToQuery}
              />
            </div>
          )}
          <div className={styles.table_container} style={{ '--table-container-height': `${calculateTableContainerHeight(report, queryParams)}px` } as any as React.CSSProperties}>
            <table className={styles.table}>
              <ReportHeader
                columns={report.columns.filter((column) => !column.internal)}
                sortBy={queryParams.get("sort_by")}
                order={queryParams.get("sort_order")}
                setSort={(field, order) => {
                  addParamsToQuery({ sort_by: field, sort_order: order });
                }}
              />
              <tbody className={styles.table_body}>
                <ReportBody
                  reportId={reportId}
                  data={reportRows}
                  columns={report.columns.filter((column) => !column.internal)}
                  loading={loadingReportRows}
                />
              </tbody>
              <tfoot
                className={styles.table_foot}>
                {report.hasTotals && (
                  <ReportTotals
                    totals={reportTotals}
                    columns={report.columns.filter((c) => !c.internal)}
                    loading={loadingReportTotals}
                  />
                )}
              </tfoot>
            </table>
          </div>
        </div>
        {report.hasPagination && (
          <ReportPagination
            loading={loadingReportPagination}
            pageNumber={parseInt(queryParams.get('page') || "1", 10)}
            maxPageNumber={maxNumberOfPages}
            noPerPage={parseInt(queryParams.get('per_page') || "20", 10)}
            setPageNumber={(page) => {
              addParamsToQuery({ page });
            }}
            setNoPerPage={(noPerPage) => {
              addParamsToQuery({ page: 1, per_page: noPerPage });
              setMaxNumberOfPages(null);
            }}
          />
        )}
      </div>
      <FileFormatModal
        report={report}
        filters={Object.fromEntries(queryParams.entries())}
        setShowDownloadModal={setShowDownloadModal}
        isOpen={showDownloadModal}
      />
    </>
  );
};

export default ReportContent;
